import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

interface ContactFormProps {
  lang: 'en' | 'it' | 'es' | 'de' | 'fr';
}

const CONTACT_STRINGS: Record<string, {
  title: string;
  subtitle: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  submit: string;
  sending: string;
  success: string;
  error: string;
  required: string;
}> = {
  en: {
    title: 'Contact Us',
    subtitle: 'Have questions? We\'d love to hear from you.',
    name: 'Your name',
    email: 'Your email',
    subject: 'Subject (optional)',
    message: 'Your message',
    submit: 'Send Message',
    sending: 'Sending...',
    success: 'Message sent successfully! We\'ll get back to you soon.',
    error: 'Something went wrong. Please try again.',
    required: 'Please fill in all required fields.'
  },
  it: {
    title: 'Contattaci',
    subtitle: 'Hai domande? Saremo felici di sentirti.',
    name: 'Il tuo nome',
    email: 'La tua email',
    subject: 'Oggetto (opzionale)',
    message: 'Il tuo messaggio',
    submit: 'Invia Messaggio',
    sending: 'Invio in corso...',
    success: 'Messaggio inviato con successo! Ti risponderemo presto.',
    error: 'Qualcosa è andato storto. Riprova.',
    required: 'Compila tutti i campi obbligatori.'
  },
  es: {
    title: 'Contáctanos',
    subtitle: '¿Tienes preguntas? Nos encantaría saber de ti.',
    name: 'Tu nombre',
    email: 'Tu email',
    subject: 'Asunto (opcional)',
    message: 'Tu mensaje',
    submit: 'Enviar Mensaje',
    sending: 'Enviando...',
    success: '¡Mensaje enviado con éxito! Te responderemos pronto.',
    error: 'Algo salió mal. Por favor intenta de nuevo.',
    required: 'Por favor completa todos los campos requeridos.'
  },
  de: {
    title: 'Kontaktieren Sie uns',
    subtitle: 'Haben Sie Fragen? Wir freuen uns von Ihnen zu hören.',
    name: 'Ihr Name',
    email: 'Ihre E-Mail',
    subject: 'Betreff (optional)',
    message: 'Ihre Nachricht',
    submit: 'Nachricht senden',
    sending: 'Wird gesendet...',
    success: 'Nachricht erfolgreich gesendet! Wir melden uns bald.',
    error: 'Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.',
    required: 'Bitte füllen Sie alle Pflichtfelder aus.'
  },
  fr: {
    title: 'Contactez-nous',
    subtitle: 'Des questions? Nous serions ravis de vous entendre.',
    name: 'Votre nom',
    email: 'Votre email',
    subject: 'Sujet (optionnel)',
    message: 'Votre message',
    submit: 'Envoyer le message',
    sending: 'Envoi en cours...',
    success: 'Message envoyé avec succès! Nous vous répondrons bientôt.',
    error: 'Une erreur s\'est produite. Veuillez réessayer.',
    required: 'Veuillez remplir tous les champs obligatoires.'
  }
};

const ContactForm: React.FC<ContactFormProps> = ({ lang }) => {
  const t = CONTACT_STRINGS[lang] || CONTACT_STRINGS.en;
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setErrorMsg(t.required);
      setStatus('error');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setErrorMsg(t.error);
      }
    } catch {
      setStatus('error');
      setErrorMsg(t.error);
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl text-center">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <p className="text-green-700 font-medium">{t.success}</p>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl">
      <h3 className="text-xl font-black tracking-tight text-[#1D1D1F] mb-2">{t.title}</h3>
      <p className="text-black/50 font-medium mb-6">{t.subtitle}</p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder={t.name + ' *'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
          />
          <input
            type="email"
            placeholder={t.email + ' *'}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
          />
        </div>
        <input
          type="text"
          placeholder={t.subject}
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
        />
        <textarea
          placeholder={t.message + ' *'}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium resize-none"
        />
        
        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errorMsg}</span>
          </div>
        )}
        
        <button
          type="submit"
          disabled={status === 'sending'}
          className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
        >
          {status === 'sending' ? t.sending : (
            <>
              <Send className="w-4 h-4" />
              {t.submit}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
